import React from 'react';
import '@/App.css';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { Toaster } from '@/components/ui/sonner';
import '@/i18n/config';
import { BrandingProvider } from '@/context/BrandingContext';
import { CurrencyProvider } from '@/context/CurrencyContext';
import { SidebarProvider } from '@/context/SidebarContext';

// Pages
import LandingPage from '@/pages/LandingPage';
import CostSavingsHub from '@/pages/CostSavingsHub';
import LoginPage from '@/pages/LoginPage';
import AuthCallback from '@/pages/AuthCallback';
import CommandCenter from '@/pages/CommandCenter';
import ImportStudio from '@/pages/ImportStudio';
import BOMIntelligenceHub from '@/pages/BOMIntelligenceHub';
import BOMDetailPage from '@/pages/BOMDetailPage';
import CostArchitect from '@/pages/CostArchitect';
import MarketPulse from '@/pages/MarketPulse';
import RiskRadar from '@/pages/RiskRadar';
import SupplierScorecard from '@/pages/SupplierScorecard';
import SmartAlternates from '@/pages/SmartAlternates';
import IntelligenceFeed from '@/pages/IntelligenceFeed';
import SuccessStories from '@/pages/SuccessStories';
import ComponentAnalysis from '@/pages/ComponentAnalysis';
import ComponentDetail from '@/pages/ComponentDetail';
import OneBid from '@/pages/OneBid';
import OneBidDetail from '@/pages/OneBidDetail';
import DesignValidate from '@/pages/DesignValidate';
import DesignProject from '@/pages/DesignProject';
import MyItems from '@/pages/MyItems';
import Reports from '@/pages/Reports';
import YourWins from '@/pages/YourWins';
import FeedbackPage from '@/pages/FeedbackPage';
import OneSourcePage from '@/pages/OneSourcePage';
import QuickOrder from '@/pages/QuickOrder';
import ConsolidateOrders from '@/pages/ConsolidateOrders';
import PartRenegotiations from '@/pages/PartRenegotiations';
import BuyTimingPage from '@/pages/BuyTimingPage';
import ActionHistory from '@/pages/ActionHistory';
import SavingsTracker from '@/pages/SavingsTracker';
import SavingsIntelligence from '@/pages/SavingsIntelligence';
import EMSSimulator from '@/pages/EMSSimulator';
import SimulatorDetail from '@/pages/SimulatorDetail';
import RDCostAnalysis from '@/pages/RDCostAnalysis';

// Admin
import UserManagement from '@/pages/admin/UserManagement';
import CompanySettings from '@/pages/admin/CompanySettings';
import Billing from '@/pages/admin/Billing';

// Components
import ProtectedRoute from '@/components/auth/ProtectedRoute';
import FloatingAIChat from '@/components/FloatingAIChat';

function AppRouter() {
  const location = useLocation();
  if (location.hash?.includes('session_id=')) return <AuthCallback />;

  return (
    <Routes>
      <Route path="/" element={<LandingPage />} />
      <Route path="/cost-savings" element={<CostSavingsHub />} />
      <Route path="/login" element={<LoginPage />} />
      <Route path="/auth/callback" element={<AuthCallback />} />

      {/* Command Center */}
      <Route path="/dashboard" element={<ProtectedRoute><CommandCenter /></ProtectedRoute>} />

      {/* Services */}
      <Route path="/component-intelligence" element={<ProtectedRoute><ComponentAnalysis /></ProtectedRoute>} />
      <Route path="/component-intelligence/:mpn" element={<ProtectedRoute><ComponentDetail /></ProtectedRoute>} />
      <Route path="/bom" element={<ProtectedRoute><BOMIntelligenceHub /></ProtectedRoute>} />
      <Route path="/bom/:bomId" element={<ProtectedRoute><BOMDetailPage /></ProtectedRoute>} />
      <Route path="/import" element={<ProtectedRoute><ImportStudio /></ProtectedRoute>} />
      <Route path="/design-center" element={<Navigate to="/design-center/validate" replace />} />
      <Route path="/design-center/new" element={<Navigate to="/design-center/validate" replace />} />
      <Route path="/design-center/validate" element={<ProtectedRoute><DesignValidate /></ProtectedRoute>} />
      <Route path="/validate/upload" element={<Navigate to="/design-center/validate" replace />} />
      <Route path="/design-center/project/:projectId" element={<ProtectedRoute><DesignProject /></ProtectedRoute>} />
      <Route path="/design-center/:projectId" element={<ProtectedRoute><DesignProject /></ProtectedRoute>} />
      <Route path="/my-items" element={<ProtectedRoute><MyItems /></ProtectedRoute>} />
      <Route path="/1bid" element={<ProtectedRoute><OneBid /></ProtectedRoute>} />
      <Route path="/1bid/:bidId" element={<ProtectedRoute><OneBidDetail /></ProtectedRoute>} />
      <Route path="/rfq-validator" element={<Navigate to="/1bid" replace />} />

      {/* Cost Reduction */}
      <Route path="/supplier-improvements" element={<ProtectedRoute><SupplierScorecard /></ProtectedRoute>} />
      <Route path="/renegotiations" element={<ProtectedRoute><PartRenegotiations /></ProtectedRoute>} />
      <Route path="/buy-timing" element={<ProtectedRoute><BuyTimingPage /></ProtectedRoute>} />
      <Route path="/alternates" element={<ProtectedRoute><SmartAlternates /></ProtectedRoute>} />
      <Route path="/risks" element={<ProtectedRoute><RiskRadar /></ProtectedRoute>} />
      <Route path="/consolidate" element={<ProtectedRoute><ConsolidateOrders /></ProtectedRoute>} />

      {/* AI Solutions */}
      <Route path="/predictions" element={<ProtectedRoute><MarketPulse /></ProtectedRoute>} />
      <Route path="/cost-architect" element={<ProtectedRoute><CostArchitect /></ProtectedRoute>} />
      <Route path="/rd-cost-analysis" element={<ProtectedRoute><RDCostAnalysis /></ProtectedRoute>} />

      {/* Sourcing */}
      <Route path="/1source" element={<ProtectedRoute><OneSourcePage /></ProtectedRoute>} />
      <Route path="/1spot" element={<ProtectedRoute><QuickOrder /></ProtectedRoute>} />

      {/* Engage */}
      <Route path="/news" element={<ProtectedRoute><IntelligenceFeed /></ProtectedRoute>} />
      <Route path="/success-stories" element={<ProtectedRoute><SuccessStories /></ProtectedRoute>} />
      <Route path="/team-savings" element={<ProtectedRoute><YourWins /></ProtectedRoute>} />
      <Route path="/value-delivered" element={<ProtectedRoute><SavingsIntelligence /></ProtectedRoute>} />
      <Route path="/feedback" element={<ProtectedRoute><FeedbackPage /></ProtectedRoute>} />

      {/* Action Center */}
      <Route path="/actions" element={<ProtectedRoute><ActionHistory /></ProtectedRoute>} />
      <Route path="/savings-tracker" element={<ProtectedRoute><SavingsTracker /></ProtectedRoute>} />

      {/* Reports & Admin */}
      <Route path="/reports" element={<ProtectedRoute><Reports /></ProtectedRoute>} />
      <Route path="/admin/users" element={<ProtectedRoute><UserManagement /></ProtectedRoute>} />
      <Route path="/admin/company" element={<ProtectedRoute><CompanySettings /></ProtectedRoute>} />
      <Route path="/settings" element={<Navigate to="/admin/company" replace />} />
      <Route path="/admin/billing" element={<ProtectedRoute><Billing /></ProtectedRoute>} />

      {/* Legacy redirects */}
      <Route path="/simulator" element={<Navigate to="/cost-architect" replace />} />
      <Route path="/distributors" element={<Navigate to="/supplier-improvements" replace />} />
      <Route path="/order" element={<Navigate to="/1spot" replace />} />
      <Route path="/your-wins" element={<Navigate to="/team-savings" replace />} />
      <Route path="/project-simulator" element={<ProtectedRoute><EMSSimulator /></ProtectedRoute>} />
      <Route path="/project-simulator/:simId" element={<ProtectedRoute><SimulatorDetail /></ProtectedRoute>} />
      <Route path="/component-analysis" element={<Navigate to="/component-intelligence" replace />} />
      <Route path="/savings" element={<Navigate to="/dashboard" replace />} />
      <Route path="/action-priority" element={<Navigate to="/dashboard" replace />} />
      <Route path="/usage-history" element={<Navigate to="/reports" replace />} />

      <Route path="/" element={<Navigate to="/dashboard" replace />} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <div className="App">
      <BrandingProvider>
        <CurrencyProvider>
          <SidebarProvider>
            <BrowserRouter>
              <AppRouter />
              <FloatingAIChat />
            </BrowserRouter>
          </SidebarProvider>
        </CurrencyProvider>
      </BrandingProvider>
      <Toaster richColors position="top-right" />
    </div>
  );
}

export default App;
